import type { IndonesianTimezone } from '../types/domain';

/**
 * FR-20 — the browser half of Web Push.
 *
 * ⚠ C2: on iPhone, Web Push works only once the PWA has been added to the
 * home screen (iOS 16.4+), and delivery is best-effort everywhere. The in-app
 * reminder cards (FR-13) stay the primary channel; this is the supplement.
 */

export const VAPID_PUBLIC_KEY: string = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

export type PushState =
  | 'unconfigured' // the build has no VAPID key
  | 'unsupported' // this browser cannot do Web Push
  | 'ios-needs-install' // iPhone/iPad in a browser tab — must be added to home screen
  | 'denied' // the user blocked notifications for the site
  | 'off' // supported and allowed, not subscribed
  | 'on';

export function isIos(userAgent: string = navigator.userAgent): boolean {
  // iPadOS 13+ reports itself as a Mac; the touch check tells them apart.
  return (
    /iPad|iPhone|iPod/.test(userAgent) ||
    (/Macintosh/.test(userAgent) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1)
  );
}

export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Where this device stands, before any subscription is looked up. */
export function basePushState(): Exclude<PushState, 'on' | 'off'> | null {
  if (!VAPID_PUBLIC_KEY) {
    return 'unconfigured';
  }
  // Checked before isPushSupported: in an iOS Safari tab PushManager is simply
  // absent, and "unsupported" would wrongly tell the parent it can never work.
  if (isIos() && !isStandalone()) {
    return 'ios-needs-install';
  }
  if (!isPushSupported()) {
    return 'unsupported';
  }
  if (Notification.permission === 'denied') {
    return 'denied';
  }
  return null;
}

/** VAPID keys are base64url; PushManager wants the raw bytes. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    out[i] = raw.charCodeAt(i);
  }
  return out;
}

export interface PushSubscriptionKeys {
  endpoint: string;
  p256dh: string;
  auth: string;
}

export function subscriptionKeys(subscription: PushSubscription): PushSubscriptionKeys {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('Langganan notifikasi tidak lengkap.');
  }
  return { endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth };
}

/** How long to wait for the service worker before saying it is not there. */
export const WORKER_READY_TIMEOUT_MS = 10000;

/**
 * The active service worker registration, or null.
 *
 * Never `navigator.serviceWorker.ready` on its own: it never settles when no
 * worker is registered — the dev server, a failed registration, some private
 * modes — and a screen awaiting it hangs on "Memeriksa..." forever.
 */
export async function getWorkerRegistration(
  timeoutMs: number = WORKER_READY_TIMEOUT_MS
): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) {
    return null;
  }
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing?.active) {
    return existing;
  }
  if (!existing) {
    return null;
  }
  // Registered but still installing: worth a bounded wait.
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>(resolve => setTimeout(() => resolve(null), timeoutMs))
  ]);
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  const registration = await getWorkerRegistration();
  return registration ? registration.pushManager.getSubscription() : null;
}

/**
 * Asks for permission and subscribes this device.
 *
 * Must be called from a tap: browsers refuse a permission prompt that no user
 * gesture asked for, and Safari refuses it outright.
 */
export async function subscribeThisDevice(): Promise<PushSubscription> {
  // Worker first: asking for a permission and then failing for want of a
  // worker leaves the parent with a granted prompt and nothing to show for it.
  const registration = await getWorkerRegistration();
  if (!registration) {
    throw new Error(
      'Aplikasi belum siap untuk notifikasi. Muat ulang halaman, lalu coba lagi.'
    );
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(
      permission === 'denied'
        ? 'Notifikasi diblokir. Izinkan notifikasi untuk situs ini di pengaturan browser.'
        : 'Izin notifikasi belum diberikan.'
    );
  }
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    return existing;
  }
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource
  });
}

/**
 * The device's zone, when it is one of Indonesia's three; otherwise WIB.
 * A family travelling abroad still gets reminders on home time.
 */
export function detectIndonesianTimezone(): IndonesianTimezone {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (zone === 'Asia/Makassar' || zone === 'Asia/Jayapura' || zone === 'Asia/Jakarta') {
      return zone;
    }
    // Other Indonesian zone names map onto the three the database accepts.
    if (zone === 'Asia/Pontianak') return 'Asia/Jakarta';
  } catch {
    // Old browsers: fall through to WIB.
  }
  return 'Asia/Jakarta';
}
